import * as E from "fp-ts/lib/Either";
import { LocalTime } from "@js-joda/core";
import React from "react";
import { FormValidationError } from "../../formUtils";
import { PlanOfCareItemId } from "../../messages";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { HashMap } from "../../utils/HashMap";
import { isDefined } from "../Generic/IsDefined";
import useModal from "../useModal";
import { Timesheet } from "./TimesheetActivity";

export type SelectedDutyTasks = HashMap<PlanOfCareItemId, { notes: string | null }>;

export interface ValidTimesheetForm {
  clockInTime: LocalTime;
  clockOutTime: LocalTime;
  selectedDutyTasks: SelectedDutyTasks;
  caregiverSignature: string;
  patientSignature: string | null;
  patientNotSignReason: string | null;
}

interface UseTimesheetFormProps {
  initialState: Timesheet;
  onSubmit: (form: ValidTimesheetForm) => void;
}

function useTimesheetForm(props: UseTimesheetFormProps) {
  const errorModal = useModal();

  const [clockInTime, setClockInTime] = React.useState<LocalTime | null>(
    props.initialState.clockInTime
  );

  const [clockOutTime, setClockOutTime] = React.useState<LocalTime | null>(
    props.initialState.clockOutTime
  );

  const [selectedDutyTasks, setSelectedDutyTasks] = React.useState<SelectedDutyTasks>(
    props.initialState.selectedDutyTasks
  );

  const [caregiverSignature, setCaregiverSignature] = React.useState<string | null>(
    props.initialState.caregiverSignature
  );

  const [patientSignature, setPatientSignature] = React.useState<string | null>(
    props.initialState.patientSignature
  );

  const [patientNotSignReason, setPatientNotSignReason] = React.useState<string | null>(
    props.initialState.patientNotSignReason
  );

  const handleSetClockInTime = (value: React.SetStateAction<LocalTime | null>) => {
    setClockInTime(value);
    clearSignatures();
  };

  const handleSetClockOutTime = (value: React.SetStateAction<LocalTime | null>) => {
    setClockOutTime(value);
    clearSignatures();
  };

  const checkTask = (id: PlanOfCareItemId, isChecked: boolean) => {
    setSelectedDutyTasks((prev) => (isChecked ? prev.set(id, { notes: null }) : prev.delete(id)));
    clearSignatures();
  };

  const setNotes = (id: PlanOfCareItemId, notes: string | null) => {
    setSelectedDutyTasks((prev) =>
      prev.set(id, { notes: isDefined(notes) && notes !== "" ? notes : null })
    );
    clearSignatures();
  };

  const clearSignatures = () => {
    setPatientSignature(null);
    setCaregiverSignature(null);
  };

  function validate(): E.Either<FormValidationError, ValidTimesheetForm> {
    if (clockInTime === null) {
      return E.left({ message: translate(STRINGS.CLOCK_IN_TIME_IS_REQUIRED) });
    }

    if (clockOutTime === null) {
      return E.left({ message: translate(STRINGS.CLOCK_OUT_TIME_IS_REQUIRED) });
    }

    if (caregiverSignature === null) {
      return E.left({ message: translate(STRINGS.CAREGIVER_SIGNATURE_IS_REQUIRED) });
    }

    if (
      patientSignature === null &&
      (patientNotSignReason === null || patientNotSignReason === "")
    ) {
      return E.left({ message: translate(STRINGS.PATIENT_SIGNATURE_IS_REQUIRED) });
    }

    return E.right({
      clockInTime,
      clockOutTime,
      selectedDutyTasks,
      caregiverSignature,
      patientSignature,
      patientNotSignReason,
    });
  }

  const submit = () => {
    const formOrError = validate();

    if (E.isLeft(formOrError)) {
      const error = formOrError.left;

      return errorModal.setModalAsError({
        title: translate(STRINGS.ERROR),
        message: error.message,
      });
    }

    props.onSubmit(formOrError.right);
  };

  return {
    state: {
      clockInTime,
      clockOutTime,
      selectedDuties: selectedDutyTasks,
      caregiverSignature,
      patientSignature,
      patientNotSignReason,
    },
    actions: {
      setClockInTime: handleSetClockInTime,
      setClockOutTime: handleSetClockOutTime,
      setCaregiverSignature: setCaregiverSignature,
      setPatientSignature: setPatientSignature,
      setPatientNotSignReason: setPatientNotSignReason,
      checkTask: checkTask,
      setNotes: setNotes,
      submit: submit,
    },
    modalProps: errorModal.modalProps,
  };
}

export default useTimesheetForm;
